import * as React from "react"
import { cn } from "src/utils/cn"

// Simple toast system — no Radix dependency needed.
// Provides: useToast() hook + <Toaster /> component to mount at app root.

const ToastContext = React.createContext(null)

let externalSetToasts = null

export function ToastProvider({ children }) {
  const [toasts, setToasts] = React.useState([])
  externalSetToasts = setToasts

  const dismiss = React.useCallback((id) => {
    setToasts((prev) => prev.filter((t) => t.id !== id))
  }, [])

  return (
    <ToastContext.Provider value={{ toasts, dismiss }}>
      {children}
    </ToastContext.Provider>
  )
}

export function useToast() {
  const toast = React.useCallback(({ title, description, variant = "default", duration = 3500 }) => {
    const id = Math.random().toString(36).slice(2)
    if (externalSetToasts) {
      externalSetToasts((prev) => [...prev, { id, title, description, variant }])
      setTimeout(() => {
        if (externalSetToasts) {
          externalSetToasts((prev) => prev.filter((t) => t.id !== id))
        }
      }, duration)
    }
  }, [])

  return { toast }
}

export function Toaster() {
  const ctx = React.useContext(ToastContext)
  if (!ctx) return null
  const { toasts, dismiss } = ctx

  return (
    <div className="fixed bottom-4 right-4 z-[100] flex flex-col gap-2 w-80">
      {toasts.map((t) => (
        <div
          key={t.id}
          className={cn(
            "flex items-start gap-3 rounded-lg border px-4 py-3 shadow-md text-sm animate-in slide-in-from-bottom-2 fade-in-0",
            t.variant === "destructive"
              ? "border-red-200 bg-red-50 text-red-800"
              : "border-slate-200 bg-white text-slate-900"
          )}
        >
          <div className="flex-1 min-w-0">
            {t.title && <p className="font-semibold leading-snug">{t.title}</p>}
            {t.description && <p className="text-[12px] text-slate-500 mt-0.5 leading-snug">{t.description}</p>}
          </div>
          <button
            onClick={() => dismiss(t.id)}
            className="shrink-0 text-slate-400 hover:text-slate-600 mt-0.5"
          >
            ✕
          </button>
        </div>
      ))}
    </div>
  )
}
